import type { ScheduleItem } from './types';
import { datesAreSame } from '$utils/dates';

/**
 * Filter a schedule down to entries that fall on a specific calendar day
 * in the viewer's local timezone.
 *
 * Uses `datesAreSame`, which compares year/month/day in the viewer's local
 * timezone, so the comparison never disagrees with how the day is rendered
 * elsewhere.
 */
export const filterMatchesByDate = (
  schedule: ScheduleItem[],
  date: Date | undefined
): ScheduleItem[] => {
  if (!date) return schedule;
  return schedule.filter((d) => datesAreSame(new Date(d.isoDate), date));
};

/**
 * Builds the "teaser" representation of past matches — a single
 * `ScheduleItem` containing only the very last stage + match on the most
 * recent past date. Used to give the user a peek at history before they
 * choose to expand the full list.
 *
 * Returns an empty array when there are no past matches.
 */
export const getLastPastMatchTeaser = (
  pastMatches: ScheduleItem[]
): ScheduleItem[] => {
  const last = pastMatches.at(-1);
  if (!last) return [];

  const lastStage = last.matchesOnDate.at(-1);
  if (!lastStage) return [last];

  return [
    {
      ...last,
      matchesOnDate: [
        {
          ...lastStage,
          matchesForStage: lastStage.matchesForStage.slice(-1),
        },
      ],
    },
  ];
};
